import React, { FCC } from 'react'
import { Route, Switch } from 'react-router-dom'
import jwtDecode from 'jwt-decode'

import {
  useFlag,
  DynamicLoadingPage,
  NotFoundView,
  routes,
  createSignOutPath,
} from '@sketch/modules-common'

import {
  useBellNotifications,
  useProjectMembershipSubscriptions,
  PrivateRoute,
  UserAcceptedToS,
  ShareRoutes,
  PreviewView,
  PersonalAuthRoute,
  PrivateWorkspaceRoute,
  WorkspaceSubscribeView,
  WorkspacePartnerSubscribeView,
  UserToWorkspaceRedirect,
  WorkspaceProfilePublicView,
  PublicationView,
  ShareSubscriptions,
  WorkspaceSubscriptions,
  RestrictedWorkspaceBannerManagerSwitch,
  WorkspaceRootRedirect,
  CommandBarProvider,
} from 'cloud-frontend'
import { PrototypeView } from 'cloud-frontend/src/modules/prototype'

import {
  WorkspaceSidebarLayoutRoutes,
  workspaceSidebarLayoutRoutePaths,
} from '../../containers/WorkspaceSidebarLayoutRoutes'

import { CoreLayoutWrapper } from './CoreLayout.styles'
import { WorkspaceThankYou } from 'cloud-frontend/src/modules/workspace/pages'
import { Location } from 'history'

const AcceptWorkspaceInviteView = React.lazy(
  () =>
    import(
      'cloud-frontend/src/modules/workspace/views/AcceptWorkspaceInviteView'
    )
)

const CreateWorkspaceView = React.lazy(
  () => import('cloud-frontend/src/modules/workspace/views/CreateWorkspaceView')
)

const CreateWorkspaceDoneView = React.lazy(
  () =>
    import('cloud-frontend/src/modules/workspace/views/CreateWorkspaceDoneView')
)

const CommunityView = React.lazy(
  () => import('cloud-frontend/src/modules/community/views/CommunityView')
)

interface InviteMembershipTokenData {
  membership_id: string
  registered_email: true
}

const getRedirectUrlForMembershipToken = (location: Location) => {
  const urlParams = new URLSearchParams(location.search)
  const token = urlParams.get('token')
  const tokenPayload = (() => {
    if (!token) {
      return {}
    }

    try {
      return jwtDecode<InviteMembershipTokenData>(token)
    } catch {
      return {}
    }
  })()

  const pathname =
    'registered_email' in tokenPayload && tokenPayload['registered_email']
      ? routes.SIGN_IN.create({})
      : routes.SIGN_UP.create({})

  return {
    pathname,
    state: { from: location },
    search: location.search,
  }
}

const CoreLayout: FCC = () => {
  useBellNotifications()
  useProjectMembershipSubscriptions()

  const isCommunityCanvasOn = useFlag('community-canvas')

  return (
    <CoreLayoutWrapper>
      <CommandBarProvider>
        <ShareSubscriptions />
        <WorkspaceSubscriptions />
        <React.Suspense fallback={<DynamicLoadingPage />}>
          <RestrictedWorkspaceBannerManagerSwitch />
          <Switch>
            {/* Redirect from Baby Cloud to the Personal-Workspace */}
            <PrivateRoute
              path={[
                routes.PERSONAL_PROJECT.template(),
                routes.DOCUMENTS.template(),
                routes.LIBRARIES.template(),
                routes.TRASH.template(),
                routes.PERSONAL_DOCUMENTS.template(),
              ]}
              component={UserToWorkspaceRedirect}
            />

            {/* Workspace Routes */}
            <PrivateRoute
              path={routes.WORKSPACE_INVITE.template()}
              component={AcceptWorkspaceInviteView}
              redirectPathIfSignOut={getRedirectUrlForMembershipToken}
            />

            <PersonalAuthRoute
              fallbackRouteIfUserOnlyHasSsoAuth={({ location }) =>
                createSignOutPath({
                  calledLocation: location!,
                  redirectLocation: {
                    pathname: routes.SIGN_IN.create({}),
                    state: { from: location },
                  },
                })
              }
              path={[
                routes.WORKSPACE_CREATE.template(),
                routes.WORKSPACE_CREATE_DONE.template(),
              ]}
              render={() => (
                <>
                  <PrivateRoute
                    exact
                    path={routes.WORKSPACE_CREATE_DONE.template()}
                    render={({ match }) => (
                      <CreateWorkspaceDoneView
                        workspaceId={match.params.workspaceId!}
                      />
                    )}
                  />
                  <PrivateRoute
                    exact
                    path={routes.WORKSPACE_CREATE.template()}
                    redirectPathIfSignOut="sign-up"
                    component={CreateWorkspaceView}
                  />
                </>
              )}
            />

            <Route
              exact
              path={workspaceSidebarLayoutRoutePaths}
              component={WorkspaceSidebarLayoutRoutes}
            />

            <PrivateWorkspaceRoute
              path={routes.WORKSPACE_SUBSCRIBE.template()}
              component={WorkspaceSubscribeView}
            />

            <PrivateRoute
              exact
              path={routes.WORKSPACE_SUBSCRIBE_PARTNER.template()}
              component={WorkspacePartnerSubscribeView}
            />

            {isCommunityCanvasOn && (
              <Route
                path={routes.COMMUNITY_CANVAS.template()}
                component={CommunityView}
              />
            )}

            <Route
              exact
              path={routes.WORKSPACE_PROFILE.template()}
              component={WorkspaceProfilePublicView}
            />

            <Route
              exact
              path={routes.WORKSPACE_PROFILE_DOCUMENT.template()}
              component={PublicationView}
            />

            <PrivateWorkspaceRoute
              exact
              path={routes.WORKSPACE_TRIAL_THANK_YOU.template()}
              component={WorkspaceThankYou}
            />
            <Route
              path={routes.PREVIEW_PROTOTYPE_PLAYER.template()}
              component={PrototypeView}
            />
            <Route
              path={routes.PREVIEW_PROTOTYPES.template()}
              component={PreviewView}
            />
            <Route
              path={[
                routes.PREVIEW_FRAME_VIEW.template(),
                routes.PREVIEW_PAGE_VIEW.template(),
                routes.PREVIEW_VIEW.template(),
              ]}
              component={PreviewView}
            />
            <Route
              path={routes.SHARE_VIEW.template()}
              component={ShareRoutes}
            />

            <PrivateWorkspaceRoute
              exact
              path={routes.WORKSPACE.template()}
              component={WorkspaceRootRedirect}
              renderErrorView={() => <NotFoundView />}
            />

            {/* Not found default */}
            <Route component={NotFoundView} />
          </Switch>
          <UserAcceptedToS />
        </React.Suspense>
      </CommandBarProvider>
    </CoreLayoutWrapper>
  )
}

export default CoreLayout
